import React, { useState, useEffect, useCallback, useRef } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress } from '@mui/material';
import { useIsMobile } from '../hooks/useIsMobile';
import { useLanguage } from '../contexts/LanguageContext';
import DesktopLayout from '../components/Desktop/DesktopLayout';
import MobileLayout from '../components/Mobile/MobileLayout';
import { searchEvents } from '../services/searchEvents';
import { useBehaviorTracking } from '../hooks/useBehaviorTracking';
import WelcomeModal from '../components/common/WelcomeModal';
import { NAV_TABS } from '../utils/categoriesConfig';
import { SEARCH_CATEGORIES } from '../config/searchCategoriesConfig';

// Helper function for deep bounds equality check with tolerance
const areBoundsEqual = (bounds1, bounds2, tolerance = 0.0001) => {
  if (!bounds1 || !bounds2) return false;
  if (!bounds1.ne || !bounds1.sw || !bounds2.ne || !bounds2.sw) return false;

  return (
    Math.abs(bounds1.ne.lat - bounds2.ne.lat) < tolerance &&
    Math.abs(bounds1.ne.lng - bounds2.ne.lng) < tolerance &&
    Math.abs(bounds1.sw.lat - bounds2.sw.lat) < tolerance &&
    Math.abs(bounds1.sw.lng - bounds2.sw.lng) < tolerance
  );
};
const DEFAULT_LAT = 13.7649161;
const DEFAULT_LNG = 100.5357104;

const Home = () => {
  const [places, setPlaces] = useState([]);
  const [filteredPlaces, setFilteredPlaces] = useState([]);
  const [childClicked, setChildClicked] = useState(null);
  const [coordinates, setCoordinates] = useState({ lat: DEFAULT_LAT, lng: DEFAULT_LNG });
  const [bounds, setBounds] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [type, setType] = useState('Explore');
  const [subCategory, setSubCategory] = useState(''); // NEW: Sub-category filter
  const [searchQuery, setSearchQuery] = useState(''); // NEW: Search query to pass to SmartEventList
  const [categoryFilter, setCategoryFilter] = useState(''); // NEW: Search bar category dropdown state

  // Initialize dates: start from 1 Jan 2025, end at today + 180 days
  const [startDate, setStartDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 180);
    return d.toISOString().split('T')[0];
  });

  // Responsive detection and language context
  const isMobile = useIsMobile();
  const { language } = useLanguage();

  // Behavior tracking for search
  const { trackSearch } = useBehaviorTracking('home_search');

  // Refs for abort controller, debounce timeout, and bounds memo
  const abortControllerRef = useRef(null);
  const debounceTimeoutRef = useRef(null);
  const lastBoundsRef = useRef(null);
  const hasDoneInitialFetchRef = useRef(false); // Track if we've done the initial fetch
  const lastLanguageRef = useRef(language); // Track last language to detect changes

  // New layout state variables
  const [currentView, setCurrentView] = useState('explore');
  const [isMapEnlarged, setIsMapEnlarged] = useState(() =>
    localStorage.getItem('mapEnlarged') === 'true'
  );
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [followList, setFollowList] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [feedCoordinates, setFeedCoordinates] = useState(null); // Map center at last explicit search
  const [feedRadiusKm, setFeedRadiusKm] = useState(null);       // radius that matched in progressive expansion
  const geolocationResolvedRef = useRef(false); // allow one re-fetch after geolocation resolves
  const locationReadyRef = useRef(false);
  const [locationReady, setLocationReady] = useState(false);

  // NEW: Mobile search UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Creator profile states
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [creatorProfile, setCreatorProfile] = useState(null);

  const handleCreatorClick = useCallback((creator) => {
    console.log('👤 Creator clicked:', creator);
    setSelectedCreator(creator);
    setType('Explore');
    setCurrentView('verticalExplore');
    setSearchQuery('');
  }, []);

  useEffect(() => {
    if (!selectedCreator?.id) {
      setCreatorProfile(null);
      return;
    }
    
    const fetchCreatorProfile = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/users/${selectedCreator.id}`);
        if (response.ok) {
          const data = await response.json();
          setCreatorProfile(data);
        }
      } catch (err) {
        console.warn('Could not fetch creator profile:', err);
      }
    };
    
    fetchCreatorProfile();
  }, [selectedCreator]);

  // Derive search params from current tab
  const getTabSearchParams = useCallback((tabId) => {
    const tab = NAV_TABS.find(t => t.id === tabId);
    if (!tab) {
      // Fallback for unknown tab
      return { source: ['events'], excludeCategories: [], categoryFilter: null };
    }
    return {
      source: tab.sources,
      excludeCategories: tab.excludeCategories,
      categoryFilter: tab.categoryFilter
    };
  }, []);

  // Merge NAV_TAB params with category dropdown override.
  // When a SEARCH_CATEGORIES entry is selected its source/categoryFilter
  // override whatever the active NAV_TAB would produce.
  const getEffectiveSearchParams = useCallback((tabId, catFilter) => {
    const tabParams = getTabSearchParams(tabId);
    const selectedCat = SEARCH_CATEGORIES.find(c => c.value === catFilter);
    if (selectedCat) {
      return {
        source: selectedCat.source,
        excludeCategories: [],
        categoryFilter: selectedCat.categoryFilter,
      };
    }
    return tabParams;
  }, [getTabSearchParams]);

  const tabParams = getEffectiveSearchParams(type, categoryFilter);

  // Welcome modal state - show only once (first time user visits)
  const [showWelcomeModal, setShowWelcomeModal] = useState(() => {
    const hasCompletedOnboarding = localStorage.getItem('tvent_onboarding_completed');
    return !hasCompletedOnboarding; // Show only if user hasn't completed onboarding
  });

  // Legacy state (keep for backward compatibility) - will be removed after testing

  // Persist map enlargement state
  useEffect(() => {
    localStorage.setItem('mapEnlarged', isMapEnlarged);
  }, [isMapEnlarged]);


  // Get user location before rendering the map.
  // Wait up to 3 s for GPS; if it arrives in time the map opens directly on the user.
  // If GPS is slower, fall back to Bangkok after 3 s and re-center once GPS resolves.
  useEffect(() => {
    const markReady = (loc) => {
      if (loc) {
        setCoordinates(loc);
        setUserLocation(loc);
      }
      if (!locationReadyRef.current) {
        locationReadyRef.current = true;
        setLocationReady(true);
      } else {
        // Timer already fired with Bangkok; GPS arrived late — allow Effect #1 to re-fetch
        geolocationResolvedRef.current = true;
      }
    };

    // Fallback: render with Bangkok after 5 s if GPS hasn't arrived yet
    const timer = setTimeout(() => {
      if (!locationReadyRef.current) {
        console.log('⏱️ GPS timeout — falling back to Bangkok');
        locationReadyRef.current = true;
        setLocationReady(true);
      }
    }, 5000);

    navigator.geolocation.getCurrentPosition(
      ({ coords: { latitude, longitude } }) => {
        console.log('🌍 User location obtained:', { latitude, longitude });
        clearTimeout(timer);
        markReady({ lat: latitude, lng: longitude });
      },
      (error) => {
        console.warn('⚠️ Geolocation error:', error.message);
        clearTimeout(timer);
        markReady(null); // fall back to Bangkok
      },
      { timeout: 30000, maximumAge: 60000 }
    );

    return () => clearTimeout(timer);
  }, []);

  // Helper function to get focused item identifier
  const getFocusedItemId = useCallback((clickedIndex, placesList) => {
    if (clickedIndex === null || clickedIndex === undefined || !Array.isArray(placesList)) {
      return null;
    }
    const focusedItem = placesList[clickedIndex];
    return focusedItem?.id || focusedItem?.event_name || null;
  }, []);

  // Helper function to restore focus by id or index
  const restoreFocus = useCallback((prevFocusedId, prevFocusedIndex, newPlacesList) => {
    if (!Array.isArray(newPlacesList) || newPlacesList.length === 0) {
      return null;
    }

    // Try to find by stable ID first
    if (prevFocusedId) {
      const foundIndex = newPlacesList.findIndex(place =>
        place.id === prevFocusedId || place.event_name === prevFocusedId
      );
      if (foundIndex !== -1) {
        if (process.env.NODE_ENV === 'development') {
          console.log('🎯 Focus preserved by ID:', { prevFocusedId, newIndex: foundIndex });
        }
        return foundIndex;
      }
    }

    // Fallback to same index if in range
    if (typeof prevFocusedIndex === 'number' &&
        prevFocusedIndex >= 0 &&
        prevFocusedIndex < newPlacesList.length) {
      if (process.env.NODE_ENV === 'development') {
        console.log('🎯 Focus preserved by index:', { prevFocusedIndex });
      }
      return prevFocusedIndex;
    }

    // Focus lost - item disappeared and index out of range
    if (process.env.NODE_ENV === 'development') {
      console.log('⚠️ Focus lost - item not found:', { prevFocusedId, prevFocusedIndex, newLength: newPlacesList.length });
    }
    return null;
  }, []);

  // Fix: Enhanced updatePlaces function with better error handling, logging, and focus preservation
  const updatePlaces = useCallback(async (hits) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 updatePlaces called with:', hits);
    }
    setIsLoading(true);

    // Store previous focus state before updating places
    const prevFocusedId = getFocusedItemId(childClicked, places);
    const prevFocusedIndex = childClicked;

    try {
      if (!Array.isArray(hits)) {
        console.error('❌ updatePlaces: hits is not an array:', typeof hits);
        setPlaces([]);
        setChildClicked(null);
        return;
      }

      const validHits = hits.filter((hit) => {
        const isValid = hit &&
          (hit.event_name || hit.place_name) &&
          (hit.latitude !== null && hit.latitude !== undefined) &&
          (hit.longitude !== null && hit.longitude !== undefined);

        if (!isValid) {
          console.warn('⚠️ Invalid hit filtered out:', hit);
        }
        return isValid;
      });

      if (process.env.NODE_ENV === 'development') {
        console.log(`📍 Before focus preservation:`, { prevFocusedId, prevFocusedIndex });
        console.log(`📍 Updating places: ${hits.length} total → ${validHits.length} valid events`);
        console.log('📊 Sample valid events:', validHits.slice(0, 3));
      }

      setPlaces(validHits);

      // Re-apply focus after updating places
      const newFocusIndex = restoreFocus(prevFocusedId, prevFocusedIndex, validHits);
      setChildClicked(newFocusIndex);

      if (process.env.NODE_ENV === 'development') {
        console.log(`📍 After focus preservation:`, { newFocusIndex });
      }

    } catch (error) {
      console.error('💥 Error in updatePlaces:', error);
      setPlaces([]);
      setChildClicked(null);
    } finally {
      setIsLoading(false);
    }
  }, [childClicked, places, getFocusedItemId, restoreFocus]);

  // Headless search controller - fetches data without requiring Header to be mounted
  const fetchAndUpdate = useCallback(async (reason = 'unknown') => {
    // Early return if no bounds set
    if (!bounds) {
      console.log('🔎 fetchAndUpdate: No bounds set, skipping search. Reason:', reason);
      return;
    }

    // Cancel any previous search
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      console.log('🚫 fetchAndUpdate: Aborted previous search');
    }

    // Create new abort controller for this search
    abortControllerRef.current = new AbortController();
    const currentController = abortControllerRef.current;

    console.log('🏁 fetchAndUpdate started:', { reason, type, subCategory, startDate, endDate, language, bounds: !!bounds });

    try {
      setIsLoading(true);

      const currentTabParams = getEffectiveSearchParams(type, categoryFilter);
      const baseParams = {
        bounds,
        startDate,
        endDate,
        type: currentTabParams.categoryFilter || undefined,
        subCategory,
        query: '',
        userLocation,
        language,
        source: currentTabParams.source,
        excludeCategories: currentTabParams.excludeCategories,
        applyLocationFilterForAuto: true,
        apiUrl: process.env.REACT_APP_API_URL,
        signal: currentController.signal,
        logger: console
      };

      // Progressive radius expansion: 50 → 100 → 300 → 500 → 1000 km.
      // disableFallback=true prevents searchEvents from silently returning unfiltered
      // Bangkok results when 0 items exist in the current radius — we need genuine 0s
      // so the loop can expand to the next radius. Only the final 1000 km step allows
      // the fallback, guaranteeing the user always gets something.
      const radii = [50, 100, 300, 500, 1000];
      let events = [];
      for (const radius of radii) {
        if (currentController.signal.aborted) break;
        const isLast = radius === radii[radii.length - 1];
        events = await searchEvents({ ...baseParams, radiusKm: radius, disableFallback: !isLast });
        if (currentController.signal.aborted) break;
        if (events.length > 0) {
          console.log(`📍 fetchAndUpdate: found ${events.length} results at radius ${radius} km`);
          // Sync SmartEventList to the same location + winning radius so the list
          // shows the same area as the map markers.
          if (userLocation) {
            setFeedCoordinates(userLocation);
            setFeedRadiusKm(radius);
          }
          break;
        }
        console.log(`📍 fetchAndUpdate: 0 results at ${radius} km — expanding radius`);
      }

      // Check if search was aborted
      if (currentController.signal.aborted) {
        console.log('🚫 fetchAndUpdate: Search was cancelled');
        return;
      }

      console.log('🏁 fetchAndUpdate done:', { reason, count: events.length });

      // Update places using existing updatePlaces function
      updatePlaces(events);

      // Mark that a search has been completed
      setHasSearched(true);

    } catch (error) {
      if (error.name === 'AbortError' || currentController.signal.aborted) {
        console.log('🚫 fetchAndUpdate: Search was aborted');
        return;
      }

      console.error('💥 fetchAndUpdate error:', error);
      updatePlaces([]);
    } finally {
      setIsLoading(false);
    }
  }, [bounds, startDate, endDate, type, subCategory, userLocation, language, updatePlaces, getEffectiveSearchParams, categoryFilter]);

  // Filter places by "type" and "selectedCreator"
  useEffect(() => {
    console.log('🏷️ Filtering places by type and selectedCreator:', { type, selectedCreator });
    
    let result = places;
    if (selectedCreator) {
      result = result.filter(p => {
        const creatorName = p._source === 'review'
          ? (p.reviewer_name || p.user_name || p.creator_name || '')
          : (p.creator_name || p.reviewer_name || p.organizer || p.user_name || '');
        const creatorId = p.event_creator_id || p.venue_creator_id || p.user_id || p.reviewer_id || '';
        return (
          (selectedCreator.name && creatorName.toLowerCase() === selectedCreator.name.toLowerCase()) ||
          (selectedCreator.id && String(creatorId) === String(selectedCreator.id))
        );
      });
    }
    
    setFilteredPlaces(result);
    console.log(`📊 Filtered Places: ${result.length} of ${places.length}`);
  }, [type, places, selectedCreator]);

  // Click handler for map location link
  const handleLocationClick = useCallback((place) => {
    if (!place?.event_location) return;
    const locationName = encodeURIComponent(place.event_location);
    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${locationName}`;
    window.open(googleMapsUrl, '_blank', 'noopener,noreferrer');
  }, []);

  // Handle welcome modal close
  const handleCloseWelcomeModal = useCallback((selectedTags, lifestyleText) => {
    setShowWelcomeModal(false);
    localStorage.setItem('tvent_onboarding_completed', 'true');
    localStorage.setItem('tvent_onboarding_completed_at', new Date().toISOString());

    // TODO: Trigger permission check and save preferences
    // handleOnboardingComplete(selectedTags, lifestyleText);
  }, []);

  // Helper function to convert event ID to global index
  // Currently not used but kept for future ID-first selection enhancements
  const getGlobalIndexById = useCallback((eventId) => {
    const listToUse = filteredPlaces.length ? filteredPlaces : places;
    return listToUse.findIndex(p => p.id === eventId);
  }, [places, filteredPlaces]);

  // Marker click -> highlight on List (ID-first approach)
  const handleMarkerClick = useCallback(
    (indexOrPlaceId) => {
      // Handle both index (from navigation) and placeId (from marker clicks)
      if (indexOrPlaceId === null) {
        setChildClicked(null);
        console.log('📍 Selection cleared');
        return;
      }

      const listToUse = filteredPlaces.length ? filteredPlaces : places;

      // If it's a number within valid range, treat as index
      if (typeof indexOrPlaceId === 'number' &&
          indexOrPlaceId >= 0 &&
          indexOrPlaceId < listToUse.length) {
        setChildClicked(indexOrPlaceId);
        console.log('📍 Index selected:', { index: indexOrPlaceId });
        return;
      }

      // Otherwise, treat as placeId and find index
      const clickedIndex = listToUse.findIndex((p) => p.id === indexOrPlaceId);
      if (clickedIndex !== -1) {
        setChildClicked(clickedIndex);
        console.log('📍 Marker clicked:', { placeId: indexOrPlaceId, clickedIndex });
      } else {
        console.warn('⚠️ Place not found:', indexOrPlaceId);
      }
    },
    [places, filteredPlaces]
  );

  // Map centering function for view mode changes
  const centerOnFirstEvent = useCallback((events) => {
    if (events.length > 0) {
      const firstEvent = events[0];
      if (firstEvent.latitude && firstEvent.longitude) {
        setCoordinates({
          lat: firstEvent.latitude,
          lng: firstEvent.longitude
        });
        console.log('🎯 Centered map on first event:', firstEvent.event_name);
      }
    }
  }, []);

  // Handle view mode switching with data fetching
  const handleViewModeChange = useCallback(async (newMode) => {
    setCurrentView(newMode);
    console.log('🔄 View mode changed to:', newMode);
    
    // For now, we'll use the existing updatePlaces function
    // In the future, you can add specific API calls for each mode:
    // - following: fetch events from followed users
    // - explore: fetch recommended events (current behavior)
    // - nearby: fetch location-based events
    
    // Center map on first event after data loads (only if following list)
    if (followList) {
      setTimeout(() => {
        const currentPlaces = filteredPlaces.length ? filteredPlaces : places;
        centerOnFirstEvent(currentPlaces);
      }, 100);
    }
  }, [filteredPlaces, places, centerOnFirstEvent]);

  // onSearch handler for search overlay and map overlay - triggers manual search
  const handleSearch = useCallback(async (searchQuery = '') => {
    console.log('🔍 handleSearch called:', { searchQuery, bounds: !!bounds, language });

    // NEW: Set search query to trigger SmartEventList refresh
    setSearchQuery(searchQuery);
    // Update feed geo to current map center so the list searches the visible area
    setFeedCoordinates({ lat: coordinates.lat, lng: coordinates.lng });
    // Force refresh by incrementing trigger (ensures re-fetch even if searchQuery is same)
    setRefreshTrigger(prev => prev + 1);

    if (!bounds) {
      console.log('⚠️ handleSearch: No bounds set, cannot search');
      return;
    }

    // Cancel any previous search
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      console.log('🚫 handleSearch: Aborted previous search');
    }

    // Create new abort controller for this search
    abortControllerRef.current = new AbortController();
    const currentController = abortControllerRef.current;

    try {
      setIsLoading(true);

      const currentTabParams = getEffectiveSearchParams(type, categoryFilter);
      const events = await searchEvents({
        bounds,
        startDate,
        endDate,
        type: currentTabParams.categoryFilter || undefined,
        subCategory,
        query: searchQuery || '', // Use provided search query
        userLocation,
        language,
        source: currentTabParams.source,
        excludeCategories: currentTabParams.excludeCategories,
        apiUrl: process.env.REACT_APP_API_URL,
        signal: currentController.signal,
        logger: console
      });

      // Check if search was aborted
      if (currentController.signal.aborted) {
        console.log('🚫 handleSearch: Search was cancelled');
        return;
      }

      console.log('✅ handleSearch done:', { searchQuery, count: events.length });

      // Update places using existing updatePlaces function
      updatePlaces(events);

      // Track successful search
      trackSearch(searchQuery || '', {
        category: type,
        startDate,
        endDate,
        language,
        hasBounds: !!bounds
      }, events);

      // Mark that a search has been completed
      setHasSearched(true);

    } catch (error) {
      if (error.name === 'AbortError' || currentController.signal.aborted) {
        console.log('🚫 handleSearch: Search was aborted');
        return;
      }

      console.error('💥 handleSearch error:', error);
      updatePlaces([]);
    } finally {
      setIsLoading(false);
    }
  }, [bounds, startDate, endDate, type, subCategory, userLocation, language, updatePlaces, trackSearch, setSearchQuery, getEffectiveSearchParams, categoryFilter]);

  // Effect #1: Initial fetch when bounds first become available
  useEffect(() => {
    if (!bounds) {
      if (process.env.NODE_ENV === 'development') {
        console.log('🎯 Initial fetch effect: No bounds yet');
      }
      return;
    }

    // Debug log to see what's happening
    if (process.env.NODE_ENV === 'development') {
      console.log('[Home] effect triggered with', {
        bounds: !!bounds,
        coordinates,
        type,
        hasSearched,
        boundsSource: bounds._src,
        hasDoneInitialFetch: hasDoneInitialFetchRef.current,
      });
    }

    // OPTION B: Force the first fetch even if bounds change is non-user
    if (!hasDoneInitialFetchRef.current) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Home] 🚀 Forcing initial fetch (option B) because this is the first run');
      }
      lastBoundsRef.current = bounds;
      fetchAndUpdate('initial-fetch-forced');
      hasDoneInitialFetchRef.current = true;
      return;
    }

    // Skip if already identical to last bounds (layout remount protection)
    if (areBoundsEqual(lastBoundsRef.current, bounds)) {
      if (process.env.NODE_ENV === 'development') {
        console.log('🎯 Initial fetch effect: Bounds identical to last bounds, skipping');
      }
      return;
    }

    // After first fetch, apply the old logic
    if (!hasSearched) {
      if (process.env.NODE_ENV === 'development') {
        console.log('🎯 Initial fetch effect: First-time bounds, triggering initial fetch');
      }
      lastBoundsRef.current = bounds;
      fetchAndUpdate('initial-bounds');
    } else {
      // We have searched before, check if this is a user-initiated search
      if (bounds._src === 'user-search') {
        if (process.env.NODE_ENV === 'development') {
          console.log('🎯 Initial fetch effect: User-initiated search, triggering fetch');
        }
        lastBoundsRef.current = bounds;
        fetchAndUpdate('user-search');
      } else if (geolocationResolvedRef.current) {
        // Geolocation resolved after the initial Bangkok fetch; map has re-centered on user's
        // location and emitted new bounds — run one fetch with the correct area.
        geolocationResolvedRef.current = false;
        if (process.env.NODE_ENV === 'development') {
          console.log('[Home] 📍 Re-fetching after geolocation resolved');
        }
        lastBoundsRef.current = bounds;
        fetchAndUpdate('geolocation-resolved');
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('[Home] 🛑 Skipping fetch: non-user bounds AND initial fetch already done');
        }
        // Still update last bounds to prevent future duplicate calls
        lastBoundsRef.current = bounds;
      }
    }
  }, [bounds, hasSearched, coordinates, type]); // Removed fetchAndUpdate to break circular dependency

  // Effect #2: Immediate fetch when language changes
  useEffect(() => {
    // Skip on initial mount or if language hasn't actually changed
    if (!bounds || !hasDoneInitialFetchRef.current) {
      lastLanguageRef.current = language;
      return;
    }

    // Only trigger if language actually changed
    if (lastLanguageRef.current !== language) {
      console.log('🌐 Language changed from', lastLanguageRef.current, 'to', language, '- triggering immediate fetch');
      lastLanguageRef.current = language;
      fetchAndUpdate('language-changed');
    }
  }, [language, bounds]); // fetchAndUpdate not included to avoid infinite loop

  // Effect #3: Debounced fetch when other filters change
  useEffect(() => {
    // Don't trigger on initial mount or if bounds not set yet
    if (!bounds) {
      return;
    }

    console.log('🔄 Filter change detected:', { type, subCategory, startDate, endDate });

    // Clear previous timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Debounce filter changes by 400ms
    debounceTimeoutRef.current = setTimeout(() => {
      console.log('🏷️ Filters changed, triggering debounced fetch');
      fetchAndUpdate('filters-changed');
    }, 400);

    // Cleanup timeout on unmount
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [type, subCategory, startDate, endDate, categoryFilter]); // Removed language - handled by separate effect

  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Debug logging for component state
  useEffect(() => {
    console.log('🏠 Home component state update:', {
      placesCount: places.length,
      filteredPlacesCount: filteredPlaces.length,
      type,
      coordinates,
      bounds: bounds ? 'Set' : 'Not set',
      isLoading
    });
  }, [places, filteredPlaces, type, coordinates, bounds, isLoading]);

  return (
    <Box className="home-page" sx={{ height: '100vh', width: '100vw' }}>
      <CssBaseline />

      {/* Welcome Modal */}
      <WelcomeModal
        open={showWelcomeModal}
        onClose={handleCloseWelcomeModal}
      />

      {!locationReady ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
          <CircularProgress />
        </Box>
      ) : isMobile ? (
        <MobileLayout
          coordinates={coordinates}
          setCoordinates={setCoordinates}
          bounds={bounds}
          setBounds={setBounds}
          childClicked={childClicked}
          setChildClicked={setChildClicked}
          handleMarkerClick={handleMarkerClick}
          handleLocationClick={handleLocationClick}
          currentView={currentView}
          setCurrentView={handleViewModeChange}
          isMapEnlarged={isMapEnlarged}
          setIsMapEnlarged={setIsMapEnlarged}
          showMobileSearch={showMobileSearch}
          setShowMobileSearch={setShowMobileSearch}
          type={type}
          setType={setType}
          subCategory={subCategory}
          setSubCategory={setSubCategory}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          isSearchExpanded={isSearchExpanded}
          setIsSearchExpanded={setIsSearchExpanded}
          followList={followList}
          setFollowList={setFollowList}
          userLocation={userLocation}
          feedLat={feedCoordinates?.lat}
          feedLng={feedCoordinates?.lng}
          feedRadiusKm={feedRadiusKm}
          onSearch={handleSearch}
          searchQuery={searchQuery}
          hasSearched={hasSearched}
          source={tabParams.source}
          excludeCategories={tabParams.excludeCategories}
          categoryFilter={tabParams.categoryFilter}
          uiCategoryFilter={categoryFilter}
          setUiCategoryFilter={setCategoryFilter}
          selectedCreator={selectedCreator}
          setSelectedCreator={setSelectedCreator}
          creatorProfile={creatorProfile}
          onCreatorClick={handleCreatorClick}
        />
      ) : (
        <DesktopLayout
          coordinates={coordinates}
          setCoordinates={setCoordinates}
          bounds={bounds}
          setBounds={setBounds}
          childClicked={childClicked}
          setChildClicked={setChildClicked}
          handleMarkerClick={handleMarkerClick}
          handleLocationClick={handleLocationClick}
          currentView={currentView}
          setCurrentView={handleViewModeChange}
          type={type}
          setType={setType}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          followList={followList}
          setFollowList={setFollowList}
          userLocation={userLocation}
          feedLat={feedCoordinates?.lat}
          feedLng={feedCoordinates?.lng}
          feedRadiusKm={feedRadiusKm}
          onSearch={handleSearch}
          searchQuery={searchQuery}
          hasSearched={hasSearched}
          source={tabParams.source}
          excludeCategories={tabParams.excludeCategories}
          categoryFilter={tabParams.categoryFilter}
          uiCategoryFilter={categoryFilter}
          setUiCategoryFilter={setCategoryFilter}
          selectedCreator={selectedCreator}
          setSelectedCreator={setSelectedCreator}
          creatorProfile={creatorProfile}
          onCreatorClick={handleCreatorClick}
        />
      )}

    </Box>
  );
};

export default Home;
